// LIBS
import { s3_media_upload, s3_signed_url } from "../libs/amplify";
import { url_to_base64 } from "../helpers/url_to_base64";
import studio_config from "../configs/studio.json";
import { ContextActions, ContextState } from "../types/contextType";

export interface CreativeContextDataProps {
  GECData: GECDataProps;
  creativeDispatch: React.Dispatch<ContextActions>;
  creativeId: string;
}

interface GECDataProps {
  readOneCreativeId: ExistingCreative;
}

interface ExistingCreative extends ContextState {
  authorEmail: string;
  authorId: string;
  teamId: string;
}

interface GenerateNewImageTypeProps {
  type: string;
  url: string;
  fileName: string;
  fileId: string;
  teamId: string;
  creativeId: string;
}

export default async function set_creative_context_data({ GECData, creativeDispatch, creativeId }: CreativeContextDataProps) {
  const existingData = GECData.readOneCreativeId;
  if (!existingData) return;

  async function generate_new_image_type({ type, url, fileName, fileId, teamId, creativeId }: GenerateNewImageTypeProps) {
    let newJpgImage = await url_to_base64(url);
    let newJpgImageUrl = await s3_media_upload({
      fileKey: `${fileName}-${fileId}-${type}.jpeg`,
      mimeType: "image/jpeg",
      body: newJpgImage,
      teamId,
      creativeId,
    });
    return newJpgImageUrl;
  }

  try {
    /**
     * JA (31-07-23)
     * Sets context data for creative based on creativeId argument
     * Ensure that a fallback value is always provided to a new key added to this payload object
     * This guarantees that a creative will load successfully even if it did not previously include a key that has been recently added to the context
     */
    const payload: GECDataProps["readOneCreativeId"] = {
      creativeId,
      elementIndex: 0,
      slideIndex: 0,
      advertiserName: existingData.advertiserName,
      advertiserId: existingData.advertiserId,
      adName: existingData.adName,
      adserver: {
        // If no data, set to "adform" as default adserver.name. All creatives made before August 2022 will have null adserver object data (due to migrating from a string to an object adserver property). Once creative is re-saved (or adform tags requested), the adserver object will be added to the creative DynamoDB item. - KT 01.08.22
        name: existingData.adserver?.name || "adform",
        tagId: existingData.adserver?.tagId || "",
        campaignId: existingData.adserver?.campaignId || "",
        tagFileNames: existingData.adserver?.tagFileNames || [],
      },
      authorEmail: existingData.authorEmail,
      authorId: existingData.authorId,
      teamId: existingData.teamId,
      draggable: {
        draggedFrom: null,
        draggedTo: null,
        isDragging: false,
      },
      control: {
        backgroundFileUploadInProgress: false,
        fileUploadInProgress: false,
        autofillInProgress: false,
      },
      writeAccess: existingData.writeAccess,
      metadata: {
        format: existingData.metadata.format,
        /**
         * HTL - 30/03/23 - setting the 'default' aspect ratio to whatever is first in the supported aspect ratios array, based on the format specified.
         * This overcomes the previous issue of not having a 'picnic' aspect ratio supported on Carousel formats.
         */
        aspectRatio: existingData.metadata.aspectRatio || studio_config.formats[existingData.metadata.format as keyof typeof studio_config.formats].media.frameRatios[0].name,
        socialUrl: existingData.metadata.socialUrl,
        brandName: existingData.metadata.brandName,
        content: existingData.metadata.content,
        brandLogo: {
          bodyOriginal: await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${existingData.metadata.brandLogo.fileName}-${existingData.metadata.brandLogo.id}-original.${existingData.metadata.brandLogo.mimeType.split("/")[1]}` }),
          bodyCropped: await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${existingData.metadata.brandLogo.fileName}-${existingData.metadata.brandLogo.id}-cropped.${existingData.metadata.brandLogo.mimeType.split("/")[1]}` }),
          mimeType: existingData.metadata.brandLogo.mimeType,
          id: existingData.metadata.brandLogo.id,
          fileName: existingData.metadata.brandLogo.fileName,
          crop: {
            x: existingData.metadata.brandLogo.crop.x,
            y: existingData.metadata.brandLogo.crop.y,
          },
          zoom: existingData.metadata.brandLogo.zoom,
          dimensions: {
            width: existingData.metadata.brandLogo?.dimensions?.width ? existingData.metadata.brandLogo.dimensions.width : studio_config.formats[existingData.metadata.format as keyof typeof studio_config.formats].brandLogo.width,
            height: existingData.metadata.brandLogo?.dimensions?.height ? existingData.metadata.brandLogo.dimensions.height : studio_config.formats[existingData.metadata.format as keyof typeof studio_config.formats].brandLogo.height,
          },
          croppedAreaPixels: {
            width: existingData.metadata.brandLogo.croppedAreaPixels.width,
            height: existingData.metadata.brandLogo.croppedAreaPixels.height,
            x: existingData.metadata.brandLogo.croppedAreaPixels.x,
            y: existingData.metadata.brandLogo.croppedAreaPixels.y,
          },
        },
        // Checks here for button logo properties - as this was introduced when Clips was added to Studio, not all creatives will have this data - KT 22.08.22
        button: {
          text: existingData.metadata.button.text,
          font: {
            id: existingData.metadata.button.font?.id ? existingData.metadata.button.font.id : "",
            bodyOriginal: existingData.metadata.button.font?.fileName ? await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${existingData.metadata.button.font.fileName}-${existingData.metadata.button.font.id}-original.${existingData.metadata.button.font.mimeType.split("/")[1]}` }) : "",
            mimeType: existingData.metadata.button.font?.mimeType ? existingData.metadata.button.font.mimeType : "",
            fileName: existingData.metadata.button.font?.fileName ? existingData.metadata.button.font.fileName : "",
          },
          clickout: existingData.metadata.button.clickout,
          backgroundColor: existingData.metadata.button.backgroundColor,
          textColor: existingData.metadata.button.textColor,
          hidden: existingData.metadata.button.hidden,
          logo: {
            bodyOriginal: existingData.metadata.button.logo?.fileName ? await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${existingData.metadata.button.logo.fileName}-${existingData.metadata.button.logo.id}-original.${existingData.metadata.button.logo.mimeType.split("/")[1]}` }) : "",
            bodyCropped: existingData.metadata.button.logo?.fileName ? await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${existingData.metadata.button.logo.fileName}-${existingData.metadata.button.logo.id}-cropped.${existingData.metadata.button.logo.mimeType.split("/")[1]}` }) : "",
            mimeType: existingData.metadata.button.logo?.mimeType ? existingData.metadata.button.logo.mimeType : "",
            id: existingData.metadata.button.logo?.id ? existingData.metadata.button.logo.id : "",
            fileName: existingData.metadata.button.logo?.fileName ? existingData.metadata.button.logo.fileName : "",
            dimensions: {
              width: existingData.metadata.button.logo?.dimensions?.width ? existingData.metadata.button.logo.dimensions.width : 0,
              height: existingData.metadata.button.logo?.dimensions?.height ? existingData.metadata.button.logo.dimensions.height : 0,
            },
            crop: {
              x: existingData.metadata.button.logo?.crop.x ? existingData.metadata.button.logo.crop.x : 0,
              y: existingData.metadata.button.logo?.crop.y ? existingData.metadata.button.logo.crop.y : 0,
            },
            zoom: existingData.metadata.button.logo?.zoom ? existingData.metadata.button.logo.zoom : 0,
            croppedAreaPixels: {
              width: existingData.metadata.button.logo?.croppedAreaPixels.width ? existingData.metadata.button.logo.croppedAreaPixels.width : 0,
              height: existingData.metadata.button.logo?.croppedAreaPixels.height ? existingData.metadata.button.logo.croppedAreaPixels.height : 0,
              x: existingData.metadata.button.logo?.croppedAreaPixels.x ? existingData.metadata.button.logo.croppedAreaPixels.x : 0,
              y: existingData.metadata.button.logo?.croppedAreaPixels.y ? existingData.metadata.button.logo.croppedAreaPixels.y : 0,
            },
          },
        },
        icons: {
          backgroundColor: existingData.metadata.icons.backgroundColor || "#FFFFFF",
          audioIconPosition: existingData.metadata.icons.audioIconPosition || "Left",
          countdownIconPosition: existingData.metadata.icons.countdownIconPosition || "Right",
          audioOn: existingData.metadata.icons.audioOn,
        },
        features: {
          reactions: {
            visible: existingData.metadata.features?.reactions?.visible ?? false,
            emojis: existingData.metadata.features?.reactions?.emojis ?? ["", "", "", "", ""],
          },
        },
        slides: await Promise.all(
          existingData.metadata.slides.map(async (slide) => ({
            id: slide.id,
            slideNumber: slide.slideNumber,
            duration: slide.duration,
            content: slide.content,
            control: {
              isToggleContainerOpen: slide.slideNumber === 1 ? true : false,
            },
            button: {
              text: slide.button.text,
              font: typeof slide.button.font === "string" ? slide.button.font : "",
              clickout: slide.button.clickout,
              backgroundColor: slide.button.backgroundColor,
              textColor: slide.button.textColor,
              hidden: slide.button.hidden,
            },
            media: {
              mimeType: slide.media.mimeType,
              audio: slide.media.audio || false,
              audioExists: slide.media.audioExists || false,
              id: slide.media.id,
              fileName: slide.media.fileName,
              crop: { x: slide.media.crop.x, y: slide.media.crop.y },
              zoom: slide.media.zoom,
              bodyOriginal: await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${slide.media.fileName}-${slide.media.id}-original.${slide.media.mimeType.split("/")[1]}` }),
              bodyCropped: await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${slide.media.fileName}-${slide.media.id}-cropped.${slide.media.mimeType.split("/")[1]}` }),
              poster: (await url_to_base64((await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${slide.media.fileName}-${slide.media.id}-poster.jpeg` })) || (await generate_new_image_type({ type: "poster", url: slide.media.bodyCropped, fileName: slide.media.fileName, fileId: slide.media.id, teamId: existingData.teamId, creativeId: creativeId })))) as string,
              plainImage: (await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${slide.media.fileName}-${slide.media.id}-plainImage.jpeg` })) || (await generate_new_image_type({ type: "plainImage", url: slide.media.bodyOriginal, fileName: slide.media.fileName, fileId: slide.media.id, teamId: existingData.teamId, creativeId: creativeId })),
              croppedAreaPixels: {
                width: slide.media.croppedAreaPixels.width,
                height: slide.media.croppedAreaPixels.height,
                x: slide.media.croppedAreaPixels.x,
                y: slide.media.croppedAreaPixels.y,
              },
              videoStartRef: slide.media.videoStartRef || 0,
              originalVideoDuration: slide.media.mimeType.includes("video") ? slide.media.originalVideoDuration || slide.duration : 0,
              videoSplitFrame: slide.media.videoSplitFrame || false,
              spriteSheet: slide.media.mimeType.includes("video") ? await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${slide.media.fileName}-${slide.media.id}-spriteSheet.jpg` }) : "",
            },
            features: {
              reactions: {
                visible: slide.features?.reactions?.visible ?? false,
              },
            },
            elements: await Promise.all(
              slide.elements.map(async (element) => ({
                id: element.id,
                mimeType: element.mimeType,
                fileName: element.fileName,
                clickout: element.clickout,
                bodyOriginal: await s3_signed_url({ teamId: existingData.teamId, creativeId, fileName: `${element?.fileName}-${element?.id}-original.${element?.mimeType.split("/")[1]}` }),
                position: { x: element.position.x, y: element.position.y },
                dimensions: {
                  width: element.dimensions.width,
                  height: element.dimensions.height,
                },
                animations: element.animations.map((animation) => ({
                  id: animation.id,
                  name: animation.name,
                  valueTo: animation.valueTo,
                  valueFrom: animation.valueFrom,
                  method: animation.method,
                  duration: animation.duration,
                  ease: animation.ease,
                  delay: animation.delay,
                  property: animation.property,
                })),
              })),
            ),
          })),
        ),
      },
    };

    creativeDispatch({
      type: "SET_CREATIVE_DATA",
      payload: await payload,
    });

    return true;
  } catch (error) {
    console.log(error);
    throw new Error(error);
  }
}
